/**
 * SyncQueue - Fila persistente de operações de sincronização
 *
 * Garante que operações de sync não sejam perdidas mesmo em caso de
 * falhas de rede, fechamento do app, ou outros erros.
 *
 * IMPORTANTE: Migrado de localStorage (5MB) para IndexedDB (50MB+)
 * para evitar QuotaExceededError em uso offline prolongado.
 */

import uuid from 'react-uuid'
import { syncLogger } from './SyncLogger'
import database from '../database'
import { recordError, recordEvent } from '../observability/events'

const QUEUE_STORAGE_KEY = 'sync_pending_queue'
const DEAD_LETTER_KEY = 'sync_dead_letter_queue'
const MAX_ATTEMPTS = 5
const BASE_DELAY_MS = 1000
const MAX_DELAY_MS = 5 * 60 * 1000 // 5 minutos

// ============================================================================
// CLEANUP E RETRY AUTOMÁTICO
// ============================================================================
const TEN_MINUTES_MS = 10 * 60 * 1000
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000
// Migração única da DLQ legada (itens sem motivo, todos `unknown`).
const DLQ_PRUNE_FLAG_KEY = 'dlq_prune_v1'

/**
 * Regra da DLQ (QA HML 2026-10-19, aprovada pelo Filipe):
 * - só vai para a DLQ a RECUSA do servidor (`error.kind === 'refused'`: 4xx exceto
 *   401/408/425/429) repetida em MAX_ATTEMPTS ciclos;
 * - transitório (`kind === 'transient'`: 5xx, rede/CORS, timeout, circuito aberto) não conta
 *   tentativa e nunca vai para a DLQ; fica na fila com backoff;
 * - no máximo 1 item por entidade/registros (na fila e na DLQ);
 * - item cujos registros foram aceitos depois é descartado (`discardSuperseded`).
 * O registro sujo no banco é a fonte da verdade; a fila é cópia (snapshot).
 */

/** Todos os ids de `a` estão em `b`? (item vazio nunca é "contido") */
const idsWithin = (a, b) => {
  if (!Array.isArray(a) || a.length === 0 || !Array.isArray(b)) return false
  const set = new Set(b)
  return a.every((id) => set.has(id))
}

/** O item `old` é coberto pelo `fresh`: mesma entidade e registros contidos nos dele. */
const coveredBy = (old, fresh) =>
  old?.operation?.entityType === fresh?.entityType &&
  idsWithin(old?.operation?.entryIds, fresh?.entryIds)

/**
 * Padrões de erro que indicam falha TEMPORÁRIA (pode resolver sozinha)
 */
const TEMPORARY_ERROR_PATTERNS = [
  /timeout/i,
  /network/i,
  /fetch/i,
  /econnrefused/i,
  /econnreset/i,
  /socket/i,
  /500/,
  /501/,
  /502/,
  /503/,
  /504/,
  /service.?unavailable/i,
  /internal.?server/i,
  /gateway/i,
]

/**
 * Padrões de erro que indicam falha PERMANENTE (não vai resolver sozinha)
 */
const PERMANENT_ERROR_PATTERNS = [
  /400/,
  /401/,
  /403/,
  /404/,
  /409/,
  /422/,
  /bad.?request/i,
  /unauthorized/i,
  /forbidden/i,
  /not.?found/i,
  /conflict/i,
  /unprocessable/i,
  /validation/i,
  /invalid/i,
]

/**
 * Status possíveis de uma operação na fila
 */
export const QueueStatus = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
  DEAD: 'dead',
}

class SyncQueue {
  constructor() {
    this.processing = false
    this._cache = null // Cache em memória para performance
    this._deadLetterCache = null
    this._initialized = false
    this._initPromise = null
  }

  /**
   * Inicializa e migra dados de localStorage para IndexedDB (se necessário)
   */
  async _ensureInitialized() {
    if (this._initialized) return

    // Evitar inicializações paralelas
    if (this._initPromise) {
      await this._initPromise
      return
    }

    this._initPromise = (async () => {
      try {
        // Migrar sync_pending_queue de localStorage para IndexedDB
        const oldQueue = localStorage.getItem(QUEUE_STORAGE_KEY)
        if (oldQueue) {
          await database.adapter.setLocal(QUEUE_STORAGE_KEY, oldQueue)
          localStorage.removeItem(QUEUE_STORAGE_KEY)
          recordEvent({
            level: 'info',
            category: 'sync',
            action: 'queue_migrated_to_indexeddb',
          })
        }

        const oldDeadLetter = localStorage.getItem(DEAD_LETTER_KEY)
        if (oldDeadLetter) {
          await database.adapter.setLocal(DEAD_LETTER_KEY, oldDeadLetter)
          localStorage.removeItem(DEAD_LETTER_KEY)
          recordEvent({
            level: 'info',
            category: 'sync',
            action: 'dead_letter_migrated_to_indexeddb',
          })
        }

        this._initialized = true
      } catch (e) {
        console.error('[SyncQueue] init error', e)
        recordError(e, { category: 'sync', action: 'queue_init_failed' })
        this._initialized = true
      }
    })()

    await this._initPromise
  }

  /**
   * Adiciona uma operação à fila
   */
  async enqueue(operation) {
    const queueItem = {
      id: uuid(),
      createdAt: Date.now(),
      updatedAt: Date.now(),
      attempts: 0,
      status: QueueStatus.PENDING,
      nextAttempt: Date.now(),
      lastError: null,
      operation,
    }

    // Coalesce: o novo snapshot (mesmos registros ou mais) substitui os pendentes antigos
    // da mesma entidade. Antes, cada ciclo de falha empilhava um item novo (QA: 14 para 1).
    const all = await this._getQueue()
    const replaced = all.filter(
      (item) =>
        item.status === QueueStatus.PENDING && coveredBy(item, operation),
    )
    const queue = all.filter((item) => !replaced.includes(item))
    if (replaced.length > 0) {
      queueItem.attempts = Math.max(...replaced.map((i) => i.attempts || 0))
      queueItem.transientAttempts = Math.max(
        ...replaced.map((i) => i.transientAttempts || 0),
      )
      queueItem.nextAttempt = Math.min(...replaced.map((i) => i.nextAttempt))
    }
    queue.push(queueItem)
    await this._saveQueue(queue)

    syncLogger.logQueueOperation('enqueue', operation.entityType, 1)

    return queueItem.id
  }

  /**
   * Adiciona múltiplas operações à fila
   */
  async enqueueBatch(operations) {
    const queue = await this._getQueue()
    const ids = []

    operations.forEach((operation) => {
      const queueItem = {
        id: uuid(),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        attempts: 0,
        status: QueueStatus.PENDING,
        nextAttempt: Date.now(),
        lastError: null,
        operation,
      }
      queue.push(queueItem)
      ids.push(queueItem.id)
    })

    await this._saveQueue(queue)

    syncLogger.logQueueOperation('enqueue_batch', 'mixed', operations.length)

    return ids
  }

  /**
   * Processa a fila de operações pendentes
   * @param {Function} executor - Função que executa a operação
   * @returns {Object} Resultado do processamento
   */
  async processQueue(executor) {
    // Cleanup de itens obsoletos antes de processar
    // (recovery de IN_PROGRESS travados, retry de temporários, expiração de antigos)
    try {
      await this.cleanupStaleItems()
    } catch (cleanupError) {
      console.warn('[SyncQueue] Erro no cleanup (continuando):', cleanupError)
    }

    if (this.processing) {
      syncLogger.log('QUEUE_SKIP', { reason: 'already_processing' })
      return { skipped: true }
    }

    this.processing = true
    const results = {
      processed: 0,
      success: 0,
      failed: 0,
      retrying: 0,
      dead: 0,
    }

    try {
      const queue = await this._getQueue()
      const now = Date.now()

      // Filtrar itens prontos para processamento
      const readyItems = queue.filter(
        (item) =>
          item.status === QueueStatus.PENDING && item.nextAttempt <= now,
      )

      for (const item of readyItems) {
        // Superado por um envio aceito neste mesmo laço (ver sucesso abaixo).
        if (item.status !== QueueStatus.PENDING) continue
        results.processed++

        try {
          // Marcar como em progresso
          item.status = QueueStatus.IN_PROGRESS
          item.attempts++
          item.updatedAt = Date.now()
          await this._saveQueue(queue)

          // Executar a operação
          const success = await executor(item.operation)

          if (success) {
            item.status = QueueStatus.COMPLETED
            results.success++
            syncLogger.log('QUEUE_ITEM_SUCCESS', {
              itemId: item.id,
              attempts: item.attempts,
            })
            // Pendentes mais velhos dos mesmos registros foram superados por este envio.
            queue.forEach((other) => {
              if (
                other !== item &&
                other.status === QueueStatus.PENDING &&
                other.createdAt <= item.createdAt &&
                coveredBy(other, item.operation)
              ) {
                other.status = QueueStatus.COMPLETED
              }
            })
            await this._discardSupersededFromDeadLetter(
              item.operation?.entityType,
              item.operation?.entryIds,
              item.operation?.createdAt ?? item.createdAt,
            )
          } else {
            throw new Error('Executor returned false')
          }
        } catch (error) {
          item.lastError = error.message
          item.lastErrorKind = error.kind ?? null
          item.httpStatus = error.status ?? null

          if (error.kind === 'transient') {
            // Servidor fora, rede, CORS, timeout: não é recusa. Não gasta tentativa e
            // nunca vai para a DLQ; espera com backoff (teto de MAX_DELAY_MS).
            item.attempts = Math.max(0, item.attempts - 1)
            item.transientAttempts = (item.transientAttempts || 0) + 1
            item.status = QueueStatus.PENDING
            item.nextAttempt = this._calculateNextAttempt(
              item.transientAttempts,
            )
            results.retrying++
            syncLogger.log('QUEUE_ITEM_TRANSIENT', {
              itemId: item.id,
              transientAttempts: item.transientAttempts,
              httpStatus: item.httpStatus,
            })
          } else if (item.attempts >= MAX_ATTEMPTS) {
            // Mover para dead letter queue
            item.status = QueueStatus.DEAD
            results.dead++
            await this._moveToDeadLetter(item)
            syncLogger.error('QUEUE_ITEM_DEAD', error, {
              itemId: item.id,
              attempts: item.attempts,
            })
          } else {
            // Calcular próxima tentativa com exponential backoff
            item.status = QueueStatus.PENDING
            item.nextAttempt = this._calculateNextAttempt(item.attempts)
            results.retrying++
            syncLogger.log('QUEUE_ITEM_RETRY', {
              itemId: item.id,
              attempts: item.attempts,
              nextAttempt: new Date(item.nextAttempt).toISOString(),
            })
          }
        }

        item.updatedAt = Date.now()
      }

      // Remover itens completados
      const activeQueue = queue.filter(
        (item) => item.status !== QueueStatus.COMPLETED,
      )
      await this._saveQueue(activeQueue)

      syncLogger.log('QUEUE_PROCESS_COMPLETE', results)
    } finally {
      this.processing = false
    }

    return results
  }

  /**
   * Calcula o próximo momento de tentativa usando exponential backoff
   */
  _calculateNextAttempt(attempts) {
    const delay = Math.min(
      BASE_DELAY_MS * Math.pow(2, attempts - 1),
      MAX_DELAY_MS,
    )
    // Adicionar jitter de até 10% para evitar thundering herd
    const jitter = delay * 0.1 * Math.random()
    return Date.now() + delay + jitter
  }

  /**
   * Classifica o tipo de erro (temporário vs permanente)
   */
  _classifyError(errorMessage) {
    if (!errorMessage) return 'unknown'

    const errorStr = String(errorMessage)

    // Verificar se é erro permanente primeiro (mais específico)
    for (const pattern of PERMANENT_ERROR_PATTERNS) {
      if (pattern.test(errorStr)) {
        return 'permanent'
      }
    }

    // Verificar se é erro temporário
    for (const pattern of TEMPORARY_ERROR_PATTERNS) {
      if (pattern.test(errorStr)) {
        return 'temporary'
      }
    }

    return 'unknown'
  }

  /**
   * Move item para dead letter queue com classificação de erro
   */
  async _moveToDeadLetter(item) {
    // Sem duplicata: o item novo substitui os da mesma entidade/registros.
    const deadLetter = (await this._getDeadLetterQueue()).filter(
      (old) => !coveredBy(old, item.operation),
    )
    const errorType =
      item.lastErrorKind === 'refused'
        ? 'permanent'
        : this._classifyError(item.lastError)

    deadLetter.push({
      ...item,
      movedAt: Date.now(),
      errorType, // "temporary", "permanent", ou "unknown"
      retryCount: 0, // Contador de retries automáticos na dead letter
    })

    // Manter apenas últimos 100 itens no dead letter
    let evictedCount = 0
    const oldestEvictedAt =
      deadLetter.length > 100 ? deadLetter[0]?.movedAt : undefined
    while (deadLetter.length > 100) {
      deadLetter.shift()
      evictedCount++
    }
    if (evictedCount > 0) {
      void recordEvent({
        level: 'warn',
        category: 'sync',
        action: 'dead_letter_overflow_eviction',
        payload: { evictedCount, oldestEvictedAt, dlqSize: 100 },
      })
    }

    await this._saveDeadLetterQueue(deadLetter)

    syncLogger.log('DEAD_LETTER_ADDED', {
      itemId: item.id,
      errorType,
      lastError: item.lastError,
    })

    void recordEvent({
      level: 'warn',
      category: 'sync',
      action: 'dead_letter_added',
      payload: {
        itemId: item.id,
        entityType: item.operation?.entityType,
        lastError: item.lastError,
        errorType,
        attempts: item.attempts,
      },
    })
  }

  /**
   * Remove da DLQ os itens da entidade cujos registros estão todos em `ids` e que são
   * anteriores a `acceptedAt`: o servidor aceitou um envio mais novo desses registros.
   */
  async _discardSupersededFromDeadLetter(entityType, ids, acceptedAt) {
    if (!entityType || !Array.isArray(ids) || ids.length === 0) return 0
    const deadLetter = await this._getDeadLetterQueue()
    const keep = deadLetter.filter(
      (item) =>
        !(
          item.operation?.entityType === entityType &&
          idsWithin(item.operation?.entryIds, ids) &&
          (item.operation?.createdAt ?? item.createdAt) <= acceptedAt
        ),
    )
    const removed = deadLetter.length - keep.length
    if (removed > 0) await this._saveDeadLetterQueue(keep)
    return removed
  }

  /**
   * O servidor aceitou (2xx) o estado de `ids` enviado em `acceptedAt`: descarta da fila
   * (pendentes) e da DLQ os snapshots mais velhos desses registros. Não chamar de dentro
   * do processQueue (ele regrava a fila no fim; lá o descarte é feito em memória).
   */
  async discardSuperseded(entityType, ids, { acceptedAt = Date.now() } = {}) {
    if (!entityType || !Array.isArray(ids) || ids.length === 0) {
      return { removedQueue: 0, removedDlq: 0 }
    }
    const queue = await this._getQueue()
    const keepQueue = queue.filter(
      (item) =>
        !(
          item.status === QueueStatus.PENDING &&
          item.operation?.entityType === entityType &&
          idsWithin(item.operation?.entryIds, ids) &&
          item.createdAt <= acceptedAt
        ),
    )
    const removedQueue = queue.length - keepQueue.length
    if (removedQueue > 0) await this._saveQueue(keepQueue)
    const removedDlq = await this._discardSupersededFromDeadLetter(
      entityType,
      ids,
      acceptedAt,
    )
    if (removedQueue > 0 || removedDlq > 0) {
      syncLogger.log('QUEUE_SUPERSEDED_DISCARDED', {
        entityType,
        removedQueue,
        removedDlq,
      })
      void recordEvent({
        level: 'info',
        category: 'sync',
        action: 'dlq_superseded',
        payload: { entityType, removedQueue, removedDlq },
      })
    }
    return { removedQueue, removedDlq }
  }

  /**
   * Migração única por aparelho da DLQ legada (antes da regra acima: tudo `unknown`, um
   * item por ciclo de falha). Descarta os itens sem `httpStatus` cujos registros existem
   * todos no aparelho: o sujo é reenviado do estado atual pelo ciclo normal (se for recusa
   * de verdade, volta classificado em ~5 ciclos) e o já aceito é obsoleto. Mantém item com
   * registro ausente (pode ser a única cópia) ou sem `entryIds`.
   * @param {(entityType: string, ids: string[]) => Promise<boolean>} entriesExist
   */
  async pruneLegacyDeadLetter(entriesExist) {
    await this._ensureInitialized()
    if ((await database.adapter.getLocal(DLQ_PRUNE_FLAG_KEY)) === 'done') {
      return null
    }
    const deadLetter = await this._getDeadLetterQueue()
    const keep = []
    for (const item of deadLetter) {
      const ids = item.operation?.entryIds
      const legacy = item.httpStatus == null
      let drop = false
      if (legacy && Array.isArray(ids) && ids.length > 0) {
        try {
          drop = await entriesExist(item.operation?.entityType, ids)
        } catch {
          drop = false
        }
      }
      if (!drop) keep.push(item)
    }
    // Duplicatas que sobraram: fica o mais novo de cada entidade/registros.
    const deduped = keep.filter(
      (item, i) =>
        !keep.some(
          (other, j) =>
            j > i &&
            other.operation?.entityType === item.operation?.entityType &&
            idsWithin(item.operation?.entryIds, other.operation?.entryIds),
        ),
    )
    await this._saveDeadLetterQueue(deduped)
    await database.adapter.setLocal(DLQ_PRUNE_FLAG_KEY, 'done')
    const result = { before: deadLetter.length, after: deduped.length }
    syncLogger.log('DEAD_LETTER_PRUNED_LEGACY', result)
    void recordEvent({
      level: 'info',
      category: 'sync',
      action: 'dlq_pruned_legacy',
      payload: result,
    })
    return result
  }

  /**
   * Limpa itens obsoletos e faz recovery de itens travados
   * Deve ser chamado periodicamente (ex: no início de cada processQueue)
   */
  async cleanupStaleItems() {
    const now = Date.now()
    const queue = await this._getQueue()
    const deadLetter = await this._getDeadLetterQueue()

    let recovered = 0
    let expiredFromDeadLetter = 0
    let retriedFromDeadLetter = 0

    // ========================================
    // 1. Recovery: IN_PROGRESS > 10min volta para PENDING
    // ========================================
    queue.forEach((item) => {
      if (
        item.status === QueueStatus.IN_PROGRESS &&
        now - item.updatedAt > TEN_MINUTES_MS
      ) {
        recordEvent({
          level: 'warn',
          category: 'sync',
          action: 'queue_recovering_stale_item',
          payload: {
            itemId: item.id,
            stuckMin: Math.round((now - item.updatedAt) / 60000),
          },
        })
        item.status = QueueStatus.PENDING
        item.nextAttempt = now
        item.updatedAt = now
        recovered++
      }
    })

    // ========================================
    // 2. Dead Letter: retry automático de erros temporários após 24h
    // ========================================
    const itemsToRetry = []
    const remainingDeadLetter = deadLetter.filter((item) => {
      const age = now - item.movedAt

      if (age > THIRTY_DAYS_MS) {
        recordEvent({
          level: 'info',
          category: 'sync',
          action: 'dead_letter_expired',
          payload: {
            itemId: item.id,
            ageDays: Math.round(age / (24 * 60 * 60 * 1000)),
          },
        })
        expiredFromDeadLetter++
        return false
      }

      // Desde a regra da DLQ (topo do arquivo) transitório não entra mais aqui; fica para
      // itens legados. Reenviar snapshot é seguro graças à guarda de versão do
      // markEntriesAsSynced (syncWorkerEnhanced.js).
      if (
        item.errorType === 'temporary' &&
        age > TWENTY_FOUR_HOURS_MS &&
        (item.retryCount || 0) < 1
      ) {
        recordEvent({
          level: 'info',
          category: 'sync',
          action: 'dead_letter_auto_retry',
          payload: { itemId: item.id, lastError: item.lastError },
        })
        itemsToRetry.push(item)
        retriedFromDeadLetter++
        return false
      }

      return true
    })

    // Mover itens para retry de volta para a fila principal
    itemsToRetry.forEach((item) => {
      queue.push({
        ...item,
        status: QueueStatus.PENDING,
        attempts: 0, // Resetar tentativas
        nextAttempt: now,
        updatedAt: now,
        lastError: null,
        retryCount: (item.retryCount || 0) + 1, // Incrementar contador de retries
      })
    })

    // Salvar se houve mudanças
    if (recovered > 0 || itemsToRetry.length > 0) {
      await this._saveQueue(queue)
    }

    if (expiredFromDeadLetter > 0 || retriedFromDeadLetter > 0) {
      await this._saveDeadLetterQueue(remainingDeadLetter)
    }

    const result = {
      recovered,
      expiredFromDeadLetter,
      retriedFromDeadLetter,
    }

    if (
      recovered > 0 ||
      expiredFromDeadLetter > 0 ||
      retriedFromDeadLetter > 0
    ) {
      syncLogger.log('QUEUE_CLEANUP', result)
      recordEvent({
        level: 'info',
        category: 'sync',
        action: 'queue_cleanup_completed',
        payload: result,
      })
    }

    return result
  }

  /**
   * Retenta itens do dead letter queue (manual)
   * @param {string|null} itemId - ID específico ou null para todos
   * @param {boolean} forceAll - Se true, retenta até erros permanentes
   */
  async retryDeadLetter(itemId = null, forceAll = false) {
    const deadLetter = await this._getDeadLetterQueue()
    const queue = await this._getQueue()
    let retried = 0
    let skipped = 0

    const itemsToRetry = itemId
      ? deadLetter.filter((item) => item.id === itemId)
      : deadLetter

    const retriedIds = []

    itemsToRetry.forEach((item) => {
      if (!forceAll && item.errorType === 'permanent') {
        skipped++
        return
      }

      // Resetar para nova tentativa
      queue.push({
        ...item,
        attempts: 0,
        status: QueueStatus.PENDING,
        nextAttempt: Date.now(),
        lastError: null,
        updatedAt: Date.now(),
        retryCount: (item.retryCount || 0) + 1,
      })

      retriedIds.push(item.id)
      retried++
    })

    // Remover do dead letter apenas os que foram movidos
    const remainingDeadLetter = deadLetter.filter(
      (item) => !retriedIds.includes(item.id),
    )

    await this._saveQueue(queue)
    await this._saveDeadLetterQueue(remainingDeadLetter)

    syncLogger.log('DEAD_LETTER_RETRY', { retried, skipped, forceAll })
    if (retried > 0 || skipped > 0) {
      recordEvent({
        level: 'info',
        category: 'sync',
        action: 'dead_letter_retry',
        payload: { retried, skipped, forceAll },
      })
    }

    return { retried, skipped }
  }

  /**
   * Obtém estatísticas da fila
   */
  async getStats() {
    const queue = await this._getQueue()
    const deadLetter = await this._getDeadLetterQueue()

    const stats = {
      total: queue.length,
      pending: queue.filter((i) => i.status === QueueStatus.PENDING).length,
      inProgress: queue.filter((i) => i.status === QueueStatus.IN_PROGRESS)
        .length,
      deadLetter: {
        total: deadLetter.length,
        temporary: deadLetter.filter((i) => i.errorType === 'temporary').length,
        permanent: deadLetter.filter((i) => i.errorType === 'permanent').length,
        unknown: deadLetter.filter(
          (i) => i.errorType === 'unknown' || !i.errorType,
        ).length,
      },
      oldestItem:
        queue.length > 0 ? Math.min(...queue.map((i) => i.createdAt)) : null,
      oldestDeadLetter:
        deadLetter.length > 0
          ? Math.min(...deadLetter.map((i) => i.movedAt))
          : null,
      byEntity: {},
    }

    // Agrupar por tipo de entidade
    queue.forEach((item) => {
      const entity = item.operation?.entityType || 'unknown'
      stats.byEntity[entity] = (stats.byEntity[entity] || 0) + 1
    })

    return stats
  }

  /**
   * Obtém itens pendentes
   */
  async getPendingItems() {
    const queue = await this._getQueue()
    return queue.filter((item) => item.status === QueueStatus.PENDING)
  }

  /**
   * Obtém itens no dead letter queue
   */
  async getDeadLetterItems() {
    return await this._getDeadLetterQueue()
  }

  /**
   * Retorna o número de itens atualmente na dead letter queue.
   * Usa o cache interno via _getDeadLetterQueue — leitura barata.
   */
  async getDeadLetterCount() {
    const items = await this._getDeadLetterQueue()
    return items.length
  }

  /**
   * Remove um item específico da fila
   */
  async removeItem(itemId) {
    const queue = await this._getQueue()
    const filtered = queue.filter((item) => item.id !== itemId)

    if (filtered.length < queue.length) {
      await this._saveQueue(filtered)
      syncLogger.log('QUEUE_ITEM_REMOVED', { itemId })
      return true
    }

    return false
  }

  /**
   * Limpa toda a fila
   */
  async clearQueue() {
    await this._saveQueue([])
    syncLogger.log('QUEUE_CLEARED', {})
  }

  /**
   * Limpa o dead letter queue
   */
  async clearDeadLetter() {
    await this._saveDeadLetterQueue([])
    syncLogger.log('DEAD_LETTER_CLEARED', {})
  }

  // Métodos privados de storage (IndexedDB via WatermelonDB adapter)

  async _getQueue() {
    await this._ensureInitialized()
    try {
      // Usar cache se disponível
      if (this._cache !== null) {
        // IMPORTANTE: Retorna CÓPIA para evitar mutação externa do cache
        return [...this._cache]
      }
      const stored = await database.adapter.getLocal(QUEUE_STORAGE_KEY)
      this._cache = stored ? JSON.parse(stored) : []
      // IMPORTANTE: Retorna CÓPIA para evitar mutação externa do cache
      return [...this._cache]
    } catch (e) {
      console.warn('[SyncQueue] read queue failed', e)
      recordError(e, { category: 'sync', action: 'queue_read_failed' })
      return []
    }
  }

  async _saveQueue(queue) {
    await this._ensureInitialized()
    try {
      this._cache = queue
      await database.adapter.setLocal(QUEUE_STORAGE_KEY, JSON.stringify(queue))
    } catch (e) {
      console.error('[SyncQueue] save queue failed', e)
      recordError(e, { category: 'sync', action: 'queue_save_failed' })
    }
  }

  async _getDeadLetterQueue() {
    await this._ensureInitialized()
    try {
      // Usar cache se disponível
      if (this._deadLetterCache !== null) {
        // IMPORTANTE: Retorna CÓPIA para evitar mutação externa do cache
        return [...this._deadLetterCache]
      }
      const stored = await database.adapter.getLocal(DEAD_LETTER_KEY)
      this._deadLetterCache = stored ? JSON.parse(stored) : []
      // IMPORTANTE: Retorna CÓPIA para evitar mutação externa do cache
      return [...this._deadLetterCache]
    } catch (e) {
      console.warn('[SyncQueue] read dead letter failed', e)
      recordError(e, { category: 'sync', action: 'dead_letter_read_failed' })
      return []
    }
  }

  async _saveDeadLetterQueue(queue) {
    await this._ensureInitialized()
    try {
      this._deadLetterCache = queue
      await database.adapter.setLocal(DEAD_LETTER_KEY, JSON.stringify(queue))
    } catch (e) {
      console.error('[SyncQueue] save dead letter failed', e)
      recordError(e, { category: 'sync', action: 'dead_letter_save_failed' })
    }
  }
}

// Singleton
export const syncQueue = new SyncQueue()

// ============================================================================
// HELPERS DE DEBUG (acessíveis via console do navegador)
// ============================================================================
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  /**
   * Helpers de debug para SyncQueue - acessíveis via console
   *
   * Uso:
   *   window.__SYNC_QUEUE__.stats()        - Ver estatísticas
   *   window.__SYNC_QUEUE__.deadLetter()   - Ver itens na dead letter
   *   window.__SYNC_QUEUE__.pending()      - Ver itens pendentes
   *   window.__SYNC_QUEUE__.retry()        - Retentar erros temporários
   *   window.__SYNC_QUEUE__.retryAll()     - Retentar TODOS (incluindo permanentes)
   *   window.__SYNC_QUEUE__.cleanup()      - Forçar cleanup manual
   *   window.__SYNC_QUEUE__.clearDeadLetter() - Limpar dead letter
   */
  window.__SYNC_QUEUE__ = {
    // Ver estatísticas da fila
    stats: async () => {
      const stats = await syncQueue.getStats()
      console.log('=== SyncQueue Stats ===')
      console.table({
        'Fila Principal': {
          Total: stats.total,
          Pendentes: stats.pending,
          'Em Progresso': stats.inProgress,
        },
      })
      console.table({
        'Dead Letter': {
          Total: stats.deadLetter.total,
          'Temporários (podem ser retentados)': stats.deadLetter.temporary,
          'Permanentes (não serão retentados)': stats.deadLetter.permanent,
          Desconhecidos: stats.deadLetter.unknown,
        },
      })
      if (stats.oldestItem) {
        console.log(
          'Item mais antigo na fila:',
          new Date(stats.oldestItem).toLocaleString(),
        )
      }
      if (stats.oldestDeadLetter) {
        console.log(
          'Item mais antigo na dead letter:',
          new Date(stats.oldestDeadLetter).toLocaleString(),
        )
      }
      return stats
    },

    // Ver itens na dead letter
    deadLetter: async () => {
      const items = await syncQueue.getDeadLetterItems()
      console.log(`=== Dead Letter Queue (${items.length} itens) ===`)
      items.forEach((item, i) => {
        console.log(`\n[${i + 1}] ID: ${item.id}`)
        console.log(`    Tipo: ${item.operation?.entityType || 'unknown'}`)
        console.log(`    Erro: ${item.lastError}`)
        console.log(`    Classificação: ${item.errorType || 'unknown'}`)
        console.log(`    Movido em: ${new Date(item.movedAt).toLocaleString()}`)
        console.log(`    Retries: ${item.retryCount || 0}`)
      })
      return items
    },

    // Ver itens pendentes
    pending: async () => {
      const items = await syncQueue.getPendingItems()
      console.log(`=== Itens Pendentes (${items.length}) ===`)
      items.forEach((item, i) => {
        console.log(`\n[${i + 1}] ID: ${item.id}`)
        console.log(`    Tipo: ${item.operation?.entityType || 'unknown'}`)
        console.log(`    Tentativas: ${item.attempts}`)
        console.log(
          `    Criado em: ${new Date(item.createdAt).toLocaleString()}`,
        )
        console.log(
          `    Próxima tentativa: ${new Date(item.nextAttempt).toLocaleString()}`,
        )
      })
      return items
    },

    // Retentar erros temporários da dead letter
    retry: async () => {
      console.log('Retentando erros temporários da dead letter...')
      const result = await syncQueue.retryDeadLetter(null, false)
      console.log(
        `Resultado: ${result.retried} retentados, ${result.skipped} ignorados (permanentes)`,
      )
      return result
    },

    // Retentar TODOS os itens da dead letter (incluindo permanentes)
    retryAll: async () => {
      console.log(
        '⚠️  Retentando TODOS os itens da dead letter (incluindo erros permanentes)...',
      )
      const result = await syncQueue.retryDeadLetter(null, true)
      console.log(`Resultado: ${result.retried} retentados`)
      return result
    },

    // Forçar cleanup manual
    cleanup: async () => {
      console.log('Executando cleanup manual...')
      const result = await syncQueue.cleanupStaleItems()
      console.log('Cleanup concluído:', result)
      return result
    },

    // Limpar dead letter
    clearDeadLetter: async () => {
      console.log('⚠️  Limpando dead letter queue...')
      await syncQueue.clearDeadLetter()
      console.log('Dead letter limpa')
    },

    // Forçar injeção de item de teste na dead letter (apenas dev)
    // Uso:
    //   forceInject()              - injeta 1 item permanente
    //   forceInject('temporary')   - injeta 1 item com erro temporário
    //   forceInject('permanent', 3) - injeta 3 itens permanentes
    forceInject: async (errorType = 'permanent', count = 1) => {
      const ERROR_MESSAGES = {
        permanent: 'HTTP 422 Unprocessable Entity (test)',
        temporary: 'Network timeout (test)',
        unknown: 'Unknown error (test)',
      }
      const lastError = ERROR_MESSAGES[errorType] || ERROR_MESSAGES.permanent
      for (let i = 0; i < count; i++) {
        await syncQueue._moveToDeadLetter({
          id: `test-dlq-${Date.now()}-${i}`,
          operation: {
            entityType: 'test_forced',
            payload: { test: true },
            entryIds: [],
          },
          status: 'dead',
          attempts: 5,
          lastError,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        })
      }
      console.log(
        `✅ Injetados ${count} item(ns) na dead letter como '${errorType}'`,
      )
      console.log('Use window.__SYNC_QUEUE__.deadLetter() para inspecionar.')
    },

    // Ajuda
    help: () => {
      console.log(`
=== SyncQueue Debug Helpers ===

Comandos disponíveis:
  window.__SYNC_QUEUE__.stats()          - Ver estatísticas
  window.__SYNC_QUEUE__.deadLetter()     - Ver itens na dead letter
  window.__SYNC_QUEUE__.pending()        - Ver itens pendentes
  window.__SYNC_QUEUE__.retry()          - Retentar erros temporários
  window.__SYNC_QUEUE__.retryAll()       - Retentar TODOS (⚠️  incluindo permanentes)
  window.__SYNC_QUEUE__.cleanup()        - Forçar cleanup manual
  window.__SYNC_QUEUE__.clearDeadLetter() - Limpar dead letter (⚠️  irreversível)
  window.__SYNC_QUEUE__.forceInject(type, count) - Injetar item de teste (dev-only)
  window.__SYNC_QUEUE__.help()           - Mostrar esta ajuda

Tipos de erro:
  - temporary: timeout, 500, network (serão retentados automaticamente após 24h)
  - permanent: 400, 422, validation (NÃO serão retentados automaticamente)
  - unknown: outros erros (tratados como permanentes)
      `)
    },
  }

  console.log(
    '[SyncQueue] Debug helpers disponíveis via: window.__SYNC_QUEUE__.help()',
  )
}

export default SyncQueue
